import { Action, combineReducers, configureStore, isRejectedWithValue, Middleware, MiddlewareAPI, ThunkAction } from '@reduxjs/toolkit';

import { setupListeners } from '@reduxjs/toolkit/query';
import { combinedApi } from './combinedApi';
import correlationSlice from './slices/correlationSlice';
import harmonicBackgroundLevelSlice from './slices/harmonicBackgroundLevelSlice';
import importedMeasurementsOverviewSlice from './slices/importedMeasurementsOverviewSlice';
import measurementVisualizationSlice from './slices/measurementVisualizationSlice';
import pqIndexSlice from './slices/pqIndexSlice';
import snackbarSlice from './slices/snackbarSlice';
import trendIdentificationSlice from './slices/trendIdentificationSlice';
import tsoIdentificationSlice from './slices/tsoIdentificationSlice';
import uploadedDataSlice from './slices/uploadedDataSlice';
import managementTreeSlice from './slices/managementTreeSlice';

const RESTORE_REDUX_STATE_ACTION = 'debug/restoreReduxState';

export const rtkQueryErrorLogger: Middleware = (api: MiddlewareAPI) => (next) => (action) => {
  if (isRejectedWithValue(action)) {
    console.error('RTK Query Error:', action);
  }

  return next(action);
};

const appReducer = combineReducers({
  [combinedApi.reducerPath]: combinedApi.reducer,

  snackbar: snackbarSlice,
  uploadedData: uploadedDataSlice,
  tsoIdentification: tsoIdentificationSlice,
  measurementVisualization: measurementVisualizationSlice,
  importedMeasurementsOverview: importedMeasurementsOverviewSlice,
  pqIndex: pqIndexSlice,
  harmonicBackgroundLevel: harmonicBackgroundLevelSlice,
  correlation: correlationSlice,
  trendIdentification: trendIdentificationSlice,
  managementTree: managementTreeSlice,
});

type ReducerState = ReturnType<typeof appReducer>;

const rootReducer = (state: ReducerState | undefined, action: Action<string> & { payload?: unknown }) => {
  if (action.type === RESTORE_REDUX_STATE_ACTION && action.payload) {
    return action.payload as ReducerState;
  }

  return appReducer(state, action);
};

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(rtkQueryErrorLogger).concat(combinedApi.middleware),
});

setupListeners(store.dispatch);

declare global {
  interface Window {
    saveReduxStateToLocalStorage?: (storageKey?: string) => void;
    restoreReduxStateFromLocalStorage?: (storageKey?: string) => void;
  }
}

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.saveReduxStateToLocalStorage = (storageKey = 'reduxState') => {
    localStorage.setItem(storageKey, JSON.stringify(store.getState()));
    console.info(`Saved Redux state to localStorage key "${storageKey}"`);
  };

  window.restoreReduxStateFromLocalStorage = (storageKey = 'reduxState') => {
    const savedState = localStorage.getItem(storageKey);

    if (!savedState) {
      console.warn(`No Redux state found in localStorage key "${storageKey}"`);
      return;
    }

    try {
      const parsedState = JSON.parse(savedState) as ReducerState;
      store.dispatch({ type: RESTORE_REDUX_STATE_ACTION, payload: parsedState });
      console.info(`Restored Redux state from localStorage key "${storageKey}"`);
    } catch (error) {
      console.error('Failed to restore Redux state from localStorage:', error);
    }
  };
}

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;
